import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';

/** Where a saved preference came from — surfaced as a badge in the panel. */
export type GablePreferenceSource = 'chat' | 'settings';

export interface GablePreference {
    id: number;
    body: string;
    source: GablePreferenceSource;
    createdAt: string | null;
}

/**
 * Thin client for the Gable saved-preferences API — "always…" / "never…"
 * standing instructions the user has taught Gable, either from chat or
 * added directly in the drawer's Preferences tab.
 */
@Injectable({ providedIn: 'root' })
export class GablePreferencesService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    list(): Observable<{ preferences: GablePreference[] }> {
        const jwt = this._requireJwt();
        return this._http.get<{ preferences: GablePreference[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Preferences(jwt.orgId)),
        );
    }

    create(body: string): Observable<{ preferences: GablePreference[] }> {
        const jwt = this._requireJwt();
        return this._http.post<{ preferences: GablePreference[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Preferences(jwt.orgId)),
            this._http.serializeObject({ body }),
        );
    }

    /** Rewrites one row's body. 404 when the id isn't this admin's; 422 on a duplicate. */
    update(id: number, body: string): Observable<{ preferences: GablePreference[] }> {
        const jwt = this._requireJwt();
        return this._http.put<{ preferences: GablePreference[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Preference(jwt.orgId, id)),
            this._http.serializeObject({ body }),
        );
    }

    remove(id: number): Observable<{ preferences: GablePreference[] }> {
        const jwt = this._requireJwt();
        return this._http.delete<{ preferences: GablePreference[] }>(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.Preference(jwt.orgId, id)),
        );
    }

    private _requireJwt(): JwtLegFiClaims {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT — cannot call preferences endpoint');
        }
        return jwt;
    }
}
